import {
  computed,
  nextTick,
  onUnmounted,
  ref,
  shallowRef,
  toValue,
  watch,
  type ComponentPublicInstance,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from "vue";
import { onClickOutside } from "@vueuse/core";
import { useIntersectionObserver } from "../observer";
import {
  computeCollisionOffset,
  type GeometryAlign,
  type GeometryBounds,
  type GeometryCollision,
} from "../geometry";

/** A template ref that may point to an element or a Vue/Kendo component. */
export type PopupMenuElementRef = Ref<
  HTMLElement | ComponentPublicInstance | null
>;

export type PopupMenuTriggerMode = "button" | "row";

/** Default `escapeFocusContainerSelector` for `triggerMode: "button"`. */
const DEFAULT_ESCAPE_FOCUS_CONTAINER_SELECTOR = ".k-table-row";

export type PopupMenuOffset = {
  left: number;
  top: number;
};

/** Mirrors Kendo Popup's `anchorAlign`/`popupAlign` point shape. */
export type PopupMenuAlign = GeometryAlign;

/** Per-axis viewport collision behavior for offset-positioned popups. */
export type PopupMenuCollision = GeometryCollision;

export type PopupMenuAnchorActivation = {
  triggerType: "click" | "keyboard";
  coordinates?: {
    clientX: number;
    clientY: number;
  };
};

export type PopupMenuActivationPointOptions = {
  /** Added to pointer coordinates after they are made trigger-relative. */
  pointerOffset?: { x?: number; y?: number };
  /** Trigger-relative keyboard position. Defaults to 8px from the left and vertically centered. */
  keyboardPosition?: {
    x?: number;
    y?: number | "top" | "center" | "bottom";
  };
};

type PopupMenuAnchorBaseOptions = {
  /** Scrollable/clipping container used for out-of-view dismissal. */
  clipRoot?: MaybeRefOrGetter<Element | null>;
  /** Closes the menu after its trigger leaves the clipping container. */
  hideWhenAnchorClipped?: boolean;
  /** Intersection ratio required to keep the anchor considered visible. */
  intersectionThreshold?: number | number[];
  /** Per-axis collision behavior. Defaults to `flip` on both axes. */
  collision?: PopupMenuCollision;
  /**
   * Point on the anchor rect that `offset` is computed from. Mirrors Kendo
   * Popup's `anchorAlign` semantics. Defaults to `{ vertical: "bottom",
   * horizontal: "left" }`, matching Kendo's own default. Passing `anchorAlign`/
   * `popupAlign` directly to `<Popup>` has no effect when positioning via
   * `offset` (no `anchor` DOM ref) - Kendo's own alignment engine only runs
   * against a real anchor element - so these options exist to reproduce that
   * alignment ourselves in the computed `offset`.
   */
  anchorAlign?: PopupMenuAlign;
  /**
   * Point on the popup itself that is placed at the computed anchor point.
   * Mirrors Kendo Popup's `popupAlign` semantics. Defaults to `{ vertical:
   * "top", horizontal: "left" }`. Requires measuring the rendered popup
   * border box; until it has a size (e.g. the first open frame), non-"left"/
   * "top" alignments briefly fall back to "left"/"top".
   */
  popupAlign?: PopupMenuAlign;
};

/** Offset-positioning options for a Popup with a dynamically changing trigger. */
export type PopupMenuAnchorOptions = PopupMenuAnchorBaseOptions &
  (
    | {
        /** Tracks a pointer or keyboard activation point relative to the trigger. */
        activationPoint: true | PopupMenuActivationPointOptions;
        getRect?: never;
      }
    | {
        activationPoint?: never;
        /** Resolves a custom anchor rect from the current trigger element. */
        getRect?: (trigger: HTMLElement) => DOMRect | null;
      }
  );

/** Why the popup was closed, including the focus policy for mouse paths. */
export type PopupMenuCloseReason =
  /** Keyboard-driven menu selection (Enter/Space on a menu item); restores the configured focus target. */
  | "keyboard"
  /** Escape key; restores the configured focus target, then `escapeFocusTarget`, then the trigger. */
  | "escape"
  /** Pointer menu selection; restores focus to the trigger. */
  | "mouse-selection"
  /** Pointer interaction outside the menu; leaves browser focus unchanged. */
  | "outside-click"
  /** Interaction with the open trigger; leaves browser focus unchanged. */
  | "trigger-click"
  /** Trigger left its clipping area; closes without scrolling it back into view. */
  | "anchor-hidden"
  /** No close has been requested yet. */
  | null;
export type PopupMenuCloseTrigger = Exclude<PopupMenuCloseReason, null>;

/** Minimal event shape emitted by Kendo Menu `@select`. */
export type KendoMenuSelectEvent<
  TItem = { text?: string; id?: string; cssClass?: string; data?: unknown },
> = {
  item?: TItem;
  event?: {
    type?: string;
  } | null;
};

/** Options for configuring {@link usePopupMenu}. */
export type UsePopupMenuOptions = {
  /** Read-only reactive flag reflecting whether the popup menu is currently open. */
  isOpen: Readonly<Ref<boolean>>;
  /** Ref to the popup menu element or component. Used for outside-click detection. */
  menuRef: PopupMenuElementRef;
  /**
   * Optional measurement source for custom popup markup. The source is
   * promoted to its nearest Kendo `.k-popup` so alignment and collision use
   * the complete border box. Defaults to `menuRef`; outside-click detection
   * always uses `menuRef` directly.
   */
  popupContentRef?: PopupMenuElementRef;
  /** Ref to the trigger button element or component. Ignored by outside-click detection. */
  triggerRef: PopupMenuElementRef;
  /** Called to open the popup menu. Kendo passes its event args as a rest tuple. */
  requestShow: (...args: unknown[]) => void;
  /** Called to close the popup menu. Kendo passes its event args as a rest tuple. */
  requestHide: (...args: unknown[]) => void;
  /**
   * Optional target to focus after a keyboard-driven close.
   * Supports ordinary elements and Vue/Kendo components exposing `$el`.
   */
  focusTargetRef?: PopupMenuElementRef;
  /**
   * Resolves the focus target when it is virtualized or dynamically rendered.
   * Takes precedence over `focusTargetRef`.
   */
  resolveFocusTarget?: () => HTMLElement | null;
  /**
   * Resolves the closest matching ancestor of the trigger as the configured
   * keyboard-close focus target. Used after `resolveFocusTarget` and
   * `focusTargetRef`.
   */
  focusTargetContainerSelector?: string;
  /**
   * Resolves a fallback focus target for Escape closes when no `focusTargetRef`/
   * `resolveFocusTarget` is configured, e.g. the trigger's containing grid row so
   * keyboard users land back in row navigation instead of on the trigger button.
   * Only consulted for `triggerMode: "button"`; row triggers already restore to
   * themselves. Takes effect only for Escape, not keyboard menu selection.
   * Takes precedence over `escapeFocusContainerSelector`.
   */
  escapeFocusTarget?: () => HTMLElement | null;
  /**
   * CSS selector for the closest ancestor of the trigger (or, if the menu was
   * never opened, of `document.activeElement`) to focus on Escape. Sugar over
   * `escapeFocusTarget` for the common "collapse back into the row" case.
   * Defaults to `".k-table-row"` for `triggerMode: "button"`; harmless to leave
   * at its default for non-grid button triggers since a missing ancestor simply
   * falls through to focusing the trigger. Ignored when `escapeFocusTarget` is set.
   */
  escapeFocusContainerSelector?: string;
  /**
   * CSS selector used to find the first focusable menu item after open.
   * Defaults to `".k-menu-item:not(.k-disabled)"` for Kendo Menu.
   */
  menuItemSelector?: string;
  /**
   * Keeps `aria-expanded` in sync with `isOpen` on the resolved `triggerRef`.
   * Defaults to `true`. The consumer is always responsible for the static
   * `aria-haspopup="menu"` and baseline `aria-expanded="false"` attributes.
   */
  manageMenuTriggerAria?: boolean;
  /** Applies an accessible name to the rendered `role="menubar"` when provided. */
  menuLabel?: MaybeRefOrGetter<string | null | undefined>;
  /**
   * Declares whether `triggerRef` is a button or a `tr.k-table-row`. Required so
   * misrouted refs (e.g. a button-mode ref that resolves to a row) are never
   * silently managed.
   */
  triggerMode: PopupMenuTriggerMode;
  /** Enables offset positioning and lifecycle tracking for a dynamic trigger. */
  anchor?: PopupMenuAnchorOptions | true;
};

/** Functions returned by {@link usePopupMenu} for the action menu. */
export type UsePopupMenuReturn = {
  /** Captures an activation relative to a trigger for activation-point anchoring. */
  setAnchorActivation: (
    trigger: HTMLElement | ComponentPublicInstance | null,
    activation: PopupMenuAnchorActivation,
  ) => boolean;
  /** Opens the menu, or closes it when it is already open. */
  handleTriggerClick: (...args: unknown[]) => void;
  /**
   * Handles keyboard input on the trigger. Enter and Space open or close
   * the menu. Escape closes an open menu or moves focus to the configured target.
   */
  handleTriggerKeydown: (...args: unknown[]) => void;
  /** Closes the menu when Escape is pressed while using the menu. */
  handleMenuEscape: (event: KeyboardEvent) => void;
  /**
   * Closes the menu after an item is chosen, then runs the optional action
   * provided by the consuming component.
   */
  handleMenuSelect: (
    event: KendoMenuSelectEvent,
    onAction?: (event: KendoMenuSelectEvent) => void,
  ) => void;
  /**
   * Restores focus after the popup closes. Keyboard actions prefer the
   * configured focus target; mouse menu selections return focus to the trigger.
   */
  handlePopupClose: () => void;
  /** Bind to Kendo Popup's `offset` prop when `anchor` is enabled. */
  offset: ComputedRef<PopupMenuOffset>;
};

const resolveElement = (
  target: HTMLElement | ComponentPublicInstance | null | undefined,
) => {
  if (target instanceof HTMLElement) return target;

  const componentElement = target?.$el;
  return componentElement instanceof HTMLElement ? componentElement : null;
};

const isManagedTrigger = (
  element: HTMLElement,
  triggerMode: PopupMenuTriggerMode,
) =>
  triggerMode === "row"
    ? element.matches("tr.k-table-row")
    : !element.matches("tr.k-table-row");

const warnMismatchedTrigger = (
  element: HTMLElement,
  triggerMode: PopupMenuTriggerMode,
) => {
  console.warn(
    `usePopupMenu: triggerMode "${triggerMode}" does not match resolved element <${element.tagName.toLowerCase()}${
      element.className ? `.${element.className.split(" ").join(".")}` : ""
    }>; ARIA management skipped.`,
  );
};

const findScrollableAncestor = (element: HTMLElement) => {
  let parent = element.parentElement;
  let styledFallback: HTMLElement | null = null;

  while (parent) {
    const { overflow, overflowX, overflowY } = window.getComputedStyle(parent);
    const scrollsX = /auto|scroll|overlay/.test(`${overflow} ${overflowX}`);
    const scrollsY = /auto|scroll|overlay/.test(`${overflow} ${overflowY}`);
    if (scrollsX || scrollsY) styledFallback ??= parent;

    if (
      (scrollsX && parent.scrollWidth > parent.clientWidth) ||
      (scrollsY && parent.scrollHeight > parent.clientHeight)
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return styledFallback;
};

/**
 * Composable for accessible Kendo Popup + Menu action-menu interactions.
 *
 * It manages toggling, menu-item focus, selection modality, and contextual
 * focus restoration. The consuming component keeps ownership of menu state
 * and business-specific actions.
 *
 * @param options - Parent-owned popup state, refs, and focus configuration.
 * @returns Template event handlers for the action trigger, Kendo Menu, and Popup.
 */
export const usePopupMenu = (
  options: UsePopupMenuOptions,
): UsePopupMenuReturn => {
  const popupCloseReason = ref<PopupMenuCloseReason>(null);
  const menuItemSelector =
    options.menuItemSelector ?? ".k-menu-item:not(.k-disabled)";
  const triggerMode = options.triggerMode;
  const anchorOptions: PopupMenuAnchorOptions | undefined =
    options.anchor === true ? {} : options.anchor;
  const activationPointOptions = anchorOptions?.activationPoint
    ? anchorOptions.activationPoint === true
      ? {}
      : anchorOptions.activationPoint
    : null;
  const anchorRect = shallowRef<DOMRect | null>(null);
  const anchorActivation = shallowRef<{
    trigger: HTMLElement;
    point: { x: number; y: number };
    hasMatchedTrigger: boolean;
  } | null>(null);
  const anchorAlign: PopupMenuAlign = anchorOptions?.anchorAlign ?? {
    horizontal: "left",
    vertical: "bottom",
  };
  const popupAlign: PopupMenuAlign = anchorOptions?.popupAlign ?? {
    horizontal: "left",
    vertical: "top",
  };
  const collision: PopupMenuCollision = anchorOptions?.collision ?? {
    horizontal: "flip",
    vertical: "flip",
  };
  // Always measured (via ResizeObserver below) whenever anchor tracking is on -
  // needed both for a non-"left"/"top" popupAlign and for flip/collision
  // detection, which must compare the popup's real size against `bounds`.
  const popupRect = shallowRef<DOMRect | null>(null);

  const resolveTriggerElement = () => resolveElement(options.triggerRef.value);
  const triggerEl = computed(() => resolveTriggerElement());
  const anchorObserverTarget = computed(() =>
    anchorOptions && anchorOptions.hideWhenAnchorClipped !== false
      ? triggerEl.value
      : null,
  );
  // Viewport by default; the configured clipRoot when provided, matching the
  // same container `hideWhenAnchorClipped`'s intersection check already uses.
  const getOffsetBounds = (): GeometryBounds => {
    const clip = anchorOptions?.clipRoot
      ? toValue(anchorOptions.clipRoot)
      : null;
    if (clip) {
      const rect = clip.getBoundingClientRect();
      return {
        left: rect.left,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
      };
    }
    return {
      left: 0,
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
    };
  };
  const offset = computed<PopupMenuOffset>(() => {
    const rect = anchorRect.value;
    if (!options.isOpen.value || !rect) return { left: 0, top: 0 };

    const bounds = getOffsetBounds();
    const corner = computeCollisionOffset({
      anchor: rect,
      popup: popupRect.value,
      anchorAlign,
      popupAlign,
      bounds,
      collision,
    });

    return {
      left: corner.left + window.scrollX,
      top: corner.top + window.scrollY,
    };
  });

  const resolveConfiguredFocusTarget = () => {
    const explicitTarget =
      options.resolveFocusTarget?.() ??
      resolveElement(options.focusTargetRef?.value);
    if (explicitTarget) return explicitTarget;

    return options.focusTargetContainerSelector
      ? (resolveTriggerElement()?.closest<HTMLElement>(
          options.focusTargetContainerSelector,
        ) ?? null)
      : null;
  };

  const setAnchorActivation = (
    trigger: HTMLElement | ComponentPublicInstance | null,
    activation: PopupMenuAnchorActivation,
  ) => {
    if (!activationPointOptions) return false;

    const triggerElement = resolveElement(trigger);
    if (!triggerElement) {
      anchorActivation.value = null;
      return false;
    }

    const triggerRect = triggerElement.getBoundingClientRect();
    if (activation.triggerType === "keyboard") {
      const keyboardPosition = activationPointOptions.keyboardPosition;
      const verticalPosition = keyboardPosition?.y ?? "center";
      const y =
        typeof verticalPosition === "number"
          ? verticalPosition
          : verticalPosition === "top"
            ? 0
            : verticalPosition === "bottom"
              ? triggerRect.height
              : triggerRect.height / 2;
      anchorActivation.value = {
        trigger: triggerElement,
        point: { x: keyboardPosition?.x ?? 8, y },
        hasMatchedTrigger: false,
      };
      return true;
    }

    if (!activation.coordinates) {
      anchorActivation.value = null;
      return false;
    }

    anchorActivation.value = {
      trigger: triggerElement,
      point: {
        x:
          activation.coordinates.clientX -
          triggerRect.left +
          (activationPointOptions.pointerOffset?.x ?? 0),
        y:
          activation.coordinates.clientY -
          triggerRect.top +
          (activationPointOptions.pointerOffset?.y ?? 0),
      },
      hasMatchedTrigger: false,
    };
    return true;
  };

  /**
   * Escape-only fallback below the configured focus target: a custom resolver,
   * else the closest `escapeFocusContainerSelector` ancestor of the trigger, or
   * of `document.activeElement` when the menu was never opened (so the trigger
   * isn't yet known to a shared registry). Button mode only.
   */
  const resolveEscapeFallbackTarget = (): HTMLElement | null => {
    if (triggerMode !== "button") return null;
    if (options.escapeFocusTarget) return options.escapeFocusTarget();

    const selector =
      options.escapeFocusContainerSelector ??
      DEFAULT_ESCAPE_FOCUS_CONTAINER_SELECTOR;
    const anchor =
      resolveTriggerElement() ?? (document.activeElement as HTMLElement | null);
    return anchor?.closest<HTMLElement>(selector) ?? null;
  };

  const focusConfiguredTargetOrTrigger = () => {
    const target =
      resolveConfiguredFocusTarget() ?? resolveEscapeFallbackTarget();
    if (target) {
      target.focus();
      return true;
    }

    const trigger = resolveTriggerElement();
    trigger?.focus();
    return !!trigger;
  };

  const focusFirstEnabledMenuItem = () => {
    const menuElement = resolveElement(options.menuRef.value);
    const firstEnabledItem = menuElement?.querySelector(menuItemSelector);
    (firstEnabledItem as HTMLElement | null)?.focus({ preventScroll: true });
  };

  // Captured before requestHide(), since shared-instance triggerRefs (e.g. useActiveIdRegistry)
  // can resolve to null the instant the active id is cleared, before the Popup's async close fires.
  const pendingFocusTarget = shallowRef<HTMLElement | null>(null);
  const pendingTrigger = shallowRef<HTMLElement | null>(null);

  // Pressing Enter/Space on a focused <button> makes the browser synthesize a
  // click on keyup; since focus restoration below runs before that keyup fires,
  // the synthetic click would otherwise land on the trigger and reopen the menu.
  let suppressNextTriggerClick = false;

  /**
   * Restores focus for the reason recorded by `closePopup`, then clears the
   * pending state so a second call (e.g. a delayed Kendo Popup `@close`) is a no-op.
   */
  const restorePendingFocus = () => {
    const reason = popupCloseReason.value;
    if (reason === "keyboard" || reason === "escape") {
      const target = pendingFocusTarget.value ?? pendingTrigger.value;
      target?.focus();
      // Only "keyboard" (Enter/Space menu selection) risks the native click-on-keyup
      // reopen; Escape never synthesizes a click, so no suppression is needed there.
      if (reason === "keyboard" && target && target === pendingTrigger.value) {
        suppressNextTriggerClick = true;
      }
    } else if (popupCloseReason.value === "mouse-selection") {
      pendingTrigger.value?.focus();
    }

    popupCloseReason.value = null;
    pendingFocusTarget.value = null;
    pendingTrigger.value = null;
  };

  /** Closes the popup if open, preserving its input modality for restoration. */
  const closePopup = (reason: PopupMenuCloseTrigger) => {
    if (!options.isOpen.value) return;
    popupCloseReason.value = reason;
    pendingFocusTarget.value =
      resolveConfiguredFocusTarget() ??
      (reason === "escape" ? resolveEscapeFallbackTarget() : null);
    pendingTrigger.value = resolveTriggerElement();
    syncManagedTrigger(false);
    options.requestHide();
    // Restores focus itself rather than waiting on Kendo Popup's `@close`, which
    // fires from an exit-animation callback that isn't reliably observed for every close.
    nextTick(restorePendingFocus);
  };

  watch(
    [triggerEl, anchorActivation],
    ([trigger], _, onCleanup) => {
      if (!anchorOptions || !trigger) {
        anchorRect.value = null;
        return;
      }

      let animationFrame: number | null = null;
      const measure = () => {
        animationFrame = null;
        const activation = anchorActivation.value;
        if (activationPointOptions) {
          if (activation && activation.trigger === trigger) {
            activation.hasMatchedTrigger = true;
          } else if (activation?.hasMatchedTrigger) {
            activation.trigger = trigger;
          }
          const triggerRect = trigger.getBoundingClientRect();
          anchorRect.value =
            activation?.trigger === trigger
              ? new DOMRect(
                  triggerRect.left + activation.point.x,
                  triggerRect.top + activation.point.y,
                  0,
                  0,
                )
              : null;
          return;
        }
        anchorRect.value =
          anchorOptions.getRect?.(trigger) ?? trigger.getBoundingClientRect();
      };
      const scheduleMeasure = () => {
        if (animationFrame === null)
          animationFrame = window.requestAnimationFrame(measure);
      };
      const observer =
        typeof window.ResizeObserver === "function"
          ? new window.ResizeObserver(scheduleMeasure)
          : null;
      observer?.observe(trigger);
      window.addEventListener("scroll", scheduleMeasure, {
        capture: true,
        passive: true,
      });
      window.addEventListener("resize", scheduleMeasure, { passive: true });
      scheduleMeasure();

      onCleanup(() => {
        if (animationFrame !== null)
          window.cancelAnimationFrame(animationFrame);
        observer?.disconnect();
        window.removeEventListener("scroll", scheduleMeasure, true);
        window.removeEventListener("resize", scheduleMeasure);
      });
    },
    { immediate: true, flush: "post" },
  );

  // Needed whenever anchor tracking is on - both for a non-"left"/"top"
  // popupAlign and for flip/collision detection, which always needs the
  // popup's real size to compare against bounds. Runs post-flush since the
  // element only has a real size once open.
  const resolvePopupContentElement = () => {
    const content = resolveElement(
      options.popupContentRef?.value ?? options.menuRef.value,
    );
    return content?.closest<HTMLElement>(".k-popup") ?? content;
  };
  if (anchorOptions) {
    watch(
      options.isOpen,
      (open, _prev, onCleanup) => {
        if (!open) {
          popupRect.value = null;
          return;
        }

        let animationFrame: number | null = null;
        const measure = () => {
          animationFrame = null;
          const contentElement = resolvePopupContentElement();
          popupRect.value = contentElement?.getBoundingClientRect() ?? null;
        };
        const scheduleMeasure = () => {
          if (animationFrame === null)
            animationFrame = window.requestAnimationFrame(measure);
        };
        const contentElement = resolvePopupContentElement();
        const observer =
          contentElement && typeof window.ResizeObserver === "function"
            ? new window.ResizeObserver(scheduleMeasure)
            : null;
        observer?.observe(contentElement!);
        scheduleMeasure();

        onCleanup(() => {
          if (animationFrame !== null)
            window.cancelAnimationFrame(animationFrame);
          observer?.disconnect();
        });
      },
      { immediate: true, flush: "post" },
    );
  }

  useIntersectionObserver({
    target: anchorObserverTarget,
    root: () =>
      anchorOptions?.clipRoot
        ? toValue(anchorOptions.clipRoot)
        : triggerEl.value
          ? findScrollableAncestor(triggerEl.value)
          : null,
    threshold:
      anchorOptions?.intersectionThreshold ??
      (activationPointOptions ? 0 : 0.5),
    onChange: (entry) => {
      if (!entry.isIntersecting) closePopup("anchor-hidden");
    },
  });

  onClickOutside(options.menuRef, () => closePopup("outside-click"), {
    ignore: [triggerEl],
  });

  let previousTrigger: HTMLElement | null = null;
  let previousMenubar: HTMLElement | null = null;

  // Reverts the previous trigger to its closed baseline; aria-haspopup is the consumer's.
  const demoteManagedTrigger = () => {
    if (previousTrigger?.isConnected) {
      previousTrigger.setAttribute("aria-expanded", "false");
    }
    previousTrigger = null;
  };

  const syncManagedTrigger = (open: boolean) => {
    if (options.manageMenuTriggerAria === false) return;

    const trigger = triggerEl.value;
    if (!trigger) {
      if (previousTrigger?.isConnected) {
        previousTrigger.setAttribute("aria-expanded", open ? "true" : "false");
      }
      return;
    }

    if (!isManagedTrigger(trigger, triggerMode)) {
      warnMismatchedTrigger(trigger, triggerMode);
      demoteManagedTrigger();
      return;
    }

    if (previousTrigger && previousTrigger !== trigger) {
      demoteManagedTrigger();
    }

    trigger.setAttribute("aria-expanded", open ? "true" : "false");
    previousTrigger = trigger;
  };

  const syncMenuLabel = () => {
    const menuElement = resolveElement(options.menuRef.value);
    const menubar = menuElement?.matches('[role="menubar"]')
      ? menuElement
      : menuElement?.querySelector<HTMLElement>('[role="menubar"]');
    const label = toValue(options.menuLabel)?.trim();

    if (previousMenubar && previousMenubar !== menubar) {
      previousMenubar.removeAttribute("aria-label");
    }
    if (menubar) {
      if (label) menubar.setAttribute("aria-label", label);
      else menubar.removeAttribute("aria-label");
    }
    previousMenubar = menubar ?? null;
  };

  watch(
    [options.isOpen, options.triggerRef],
    ([open]) => {
      syncManagedTrigger(open);
      if (!open && activationPointOptions) {
        anchorActivation.value = null;
        anchorRect.value = null;
      }
    },
    { immediate: true, flush: "post" },
  );

  watch(
    [options.isOpen, options.menuRef, () => toValue(options.menuLabel)],
    syncMenuLabel,
    { immediate: true, flush: "post" },
  );

  // Resets managed ARIA attributes on unmount; aria-haspopup remains consumer-owned.
  onUnmounted(() => {
    anchorActivation.value = null;
    demoteManagedTrigger();
    previousMenubar?.removeAttribute("aria-label");
  });

  // Focuses the first enabled item whenever the menu opens, however it was opened
  // (handleTriggerClick, or a consumer driving isOpen directly for row triggers).
  // flush: "post" already waits for the Popup/Menu DOM to render, so no extra nextTick is needed.
  watch(
    options.isOpen,
    (open) => {
      if (open) focusFirstEnabledMenuItem();
    },
    { flush: "post" },
  );

  /** Toggles the menu and focuses its first enabled item after opening. */
  const handleTriggerClick = (...args: unknown[]) => {
    const context = args[0];

    if (suppressNextTriggerClick) {
      suppressNextTriggerClick = false;
      return;
    }

    if (options.isOpen.value) {
      closePopup("trigger-click");
      return;
    }

    options.requestShow(context);
  };

  /** Handles Enter, Space, and Escape on the trigger. */
  const handleTriggerKeydown = (...args: unknown[]) => {
    const event = args[0] as KeyboardEvent;
    const context = args[1];

    if (event.key === "Enter" || event.code === "Space") {
      event.preventDefault();
      handleTriggerClick(context);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();

      if (options.isOpen.value) {
        closePopup("escape");
        return;
      }

      focusConfiguredTargetOrTrigger();
    }
  };

  /** Escape handler bound inside the menu. */
  const handleMenuEscape = (event: KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    closePopup("escape");
  };

  /** Closes the menu then delegates its business action to the caller. */
  const handleMenuSelect = (
    event: KendoMenuSelectEvent,
    onAction?: (event: KendoMenuSelectEvent) => void,
  ) => {
    closePopup(
      event.event?.type?.startsWith("key") ? "keyboard" : "mouse-selection",
    );
    onAction?.(event);
  };

  /**
   * Bind to the Kendo Popup `@close` event after the owner updates `isOpen`.
   * Keyboard menu selection and Escape both try the configured focus target
   * first, Escape then falls back to `escapeFocusTarget`, and both fall back
   * to the trigger. Mouse menu selection restores the trigger, while outside
   * clicks leave focus under browser control.
   */
  // Kept as a safety net for consumers that still wire Kendo Popup's `@close`;
  // restorePendingFocus() is idempotent once closePopup's own nextTick has already run.
  const handlePopupClose = () => {
    nextTick(restorePendingFocus);
  };

  return {
    /**
     * Sets the anchor activation state, controlling whether the popup menu
     * should respond to trigger interactions.
     */
    setAnchorActivation,
    /**
     * Handles Kendo Menu selection: records keyboard or mouse modality,
     * requests popup close, then runs the optional caller-owned action.
     */
    handleMenuSelect,
    /**
     * Toggles the action menu. When opening, focuses the first enabled Kendo
     * Menu item after Vue renders the popup.
     */
    handleTriggerClick,
    /**
     * Handles trigger keyboard input: Enter and Space toggle the menu; Escape
     * closes an open menu or focuses the configured target when already closed.
     */
    handleTriggerKeydown,
    /** Closes the open menu through the keyboard path when Escape is pressed inside it. */
    handleMenuEscape,
    /**
     * Restores focus after Kendo Popup closes: keyboard menu selection and
     * Escape prefer the configured focus target (Escape then falls back to
     * `escapeFocusTarget`), mouse selections return to the trigger, and
     * outside clicks do not move focus.
     */
    handlePopupClose,
    offset,
  };
};
